import axios, { AxiosInstance } from 'axios'
import { getConfig } from '../config/Constants'
import type { ApiResponse } from '../types/api.types'
import type { CourseCurriculum } from '../types/courseCurriculum.types'

let apiInstance: AxiosInstance | null = null

function getApiInstance() {
    if (!apiInstance) {
        const config = getConfig()
        apiInstance = axios.create({
            baseURL: `${config.apiUrl}`,
            headers: {
                'Content-Type': 'application/json',
            },
        })

        // Add request interceptor to add auth token
        apiInstance.interceptors.request.use(
            (config) => {
                const token = localStorage.getItem('access_token')
                if (token) {
                    config.headers.Authorization = `Bearer ${token}`
                }
                return config
            },
            (error) => {
                return Promise.reject(error)
            }
        )
    }
    return apiInstance
}

export const courseCurriculumService = {
    getCourseCurriculumByCourseId,
    getCourseCurriculumById,
    getAllCourseCurriculums,
    createCourseCurriculum,
    updateCourseCurriculum,
    deleteCourseCurriculum,
}

/**
 * Get curriculum by course ID
 */
async function getCourseCurriculumByCourseId(courseId: string): Promise<ApiResponse<CourseCurriculum>> {
    try {
        const response = await getApiInstance().get(`/course-curriculums/course/${courseId}`)
        const curriculum = response.data?.data || response.data

        if (!curriculum) {
            return {
                success: false,
                error: 'Curriculum not found'
            }
        }

        return {
            success: true,
            data: curriculum
        }
    } catch (error: any) {
        console.error('Error fetching curriculum by course ID:', error)
        return {
            success: false,
            error: error.response?.data?.message || error.message || 'Failed to fetch curriculum'
        }
    }
}

/**
 * Get curriculum by curriculum ID
 */
async function getCourseCurriculumById(id: string): Promise<ApiResponse<CourseCurriculum>> {
    try {
        const response = await getApiInstance().get(`/course-curriculums/${id}`)
        const curriculum = response.data?.data || response.data

        if (!curriculum) {
            return {
                success: false,
                error: 'Curriculum not found'
            }
        }

        return {
            success: true,
            data: curriculum
        }
    } catch (error: any) {
        console.error('Error fetching curriculum:', error)
        return {
            success: false,
            error: error.response?.data?.message || error.message || 'Failed to fetch curriculum'
        }
    }
}

/**
 * Get all curriculums with pagination
 */
async function getAllCourseCurriculums(page = 1, pageSize = 10): Promise<ApiResponse<CourseCurriculum[]>> {
    try {
        const response = await getApiInstance().get('/course-curriculums', {
            params: { page, page_size: pageSize }
        })
        const data = response.data

        let curriculums: CourseCurriculum[] = []

        if (data.success && data.data) {
            curriculums = Array.isArray(data.data) ? data.data : []
        } else if (Array.isArray(data)) {
            curriculums = data
        }

        return {
            success: true,
            data: curriculums
        }
    } catch (error: any) {
        console.error('Error fetching curriculums:', error)
        return {
            success: false,
            error: error.response?.data?.message || error.message || 'Failed to fetch curriculums'
        }
    }
}

/**
 * Create a new curriculum (Admin only)
 */
async function createCourseCurriculum(data: Partial<CourseCurriculum>): Promise<ApiResponse<CourseCurriculum>> {
    try {
        const response = await getApiInstance().post('/course-curriculums', data)
        const curriculum = response.data?.data || response.data

        return {
            success: true,
            data: curriculum
        }
    } catch (error: any) {
        console.error('Error creating curriculum:', error)
        return {
            success: false,
            error: error.response?.data?.message || error.message || 'Failed to create curriculum'
        }
    }
}

/**
 * Update an existing curriculum (Admin only)
 */
async function updateCourseCurriculum(id: string, data: Partial<CourseCurriculum>): Promise<ApiResponse<CourseCurriculum>> {
    try {
        const response = await getApiInstance().put(`/course-curriculums/${id}`, data)
        const curriculum = response.data?.data || response.data

        return {
            success: true,
            data: curriculum
        }
    } catch (error: any) {
        console.error('Error updating curriculum:', error)
        return {
            success: false,
            error: error.response?.data?.message || error.message || 'Failed to update curriculum'
        }
    }
}

/**
 * Delete a curriculum (Admin only)
 */
async function deleteCourseCurriculum(id: string): Promise<ApiResponse<void>> {
    try {
        await getApiInstance().delete(`/course-curriculums/${id}`)

        return {
            success: true,
            data: undefined
        }
    } catch (error: any) {
        console.error('Error deleting curriculum:', error)
        return {
            success: false,
            error: error.response?.data?.message || error.message || 'Failed to delete curriculum'
        }
    }
}
